import { Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Effect, Actions, ofType } from '@ngrx/effects';
import { Observable } from 'rxjs';
import { filter, map, withLatestFrom, switchMap } from 'rxjs/operators';

import * as actions from 'app/actions';
import * as appReducer from 'app/reducer';
import { TenantServiceProxy, CommonLookupServiceProxy } from '@shared/service-proxies/service-proxies';

@Injectable()
export class RootEffects {

    @Effect()
    initStates$: Observable<Action> = this.actions$.pipe(
        ofType(actions.INIT_STATES),
        withLatestFrom(this.store.select(s => s.root.states)),
        filter(([action, states]) => states === undefined),
        map(() => new actions.LoadStatesAction())
    );

    @Effect()
    loadStates$: Observable<Action> = this.actions$.pipe(
        ofType(actions.LOAD_STATES),
        switchMap(() =>
            this.lookupService.getStates().pipe(
                map(states => new actions.StatesLoadedAction(states))
            )
        )
    );

    @Effect()
    loadAccountTypes$: Observable<Action> = this.actions$.pipe(
        ofType(actions.LOAD_ACCOUNT_TYPES),
        switchMap(() =>
            this.tenantService.getAccountTypes().pipe(
                map(accountTypes => new actions.AccountTypesLoadedAction(accountTypes))
            )
        )
    );

    @Effect()
    loadGeographicRegions$: Observable<Action> = this.actions$.pipe(
        ofType(actions.LOAD_GEOGRAPHIC_REGIONS),
        switchMap(() =>
            this.tenantService.getGeographicRegions().pipe(
                map(geographicRegions => new actions.GeographicRegionsLoadedAction(geographicRegions))
            )
        )
    );

    @Effect()
    loadIndustryTypes$: Observable<Action> = this.actions$.pipe(
        ofType(actions.LOAD_INDUSTRY_TYPES),
        switchMap(() =>
            this.tenantService.getIndustryTypes().pipe(
                map(industryTypes => new actions.IndustryTypesLoadedAction(industryTypes))
            )
        )
    );

    @Effect()
    initTransactionTypes$: Observable<Action> = this.actions$.pipe(
        ofType(actions.INIT_TRANSACTION_TYPES),
        withLatestFrom(this.store.select(s => s.root.transactionTypes)),
        map(([action, transactionTypes]) => {
            if (transactionTypes !== undefined) {
                return {
                    type: actions.FAKE_ACTION,
                    payload: '[Root] Transaction types already loaded'
                };
            }

            return new actions.LoadTransactionTypesAction();
        })
    );

    @Effect()
    loadTransactionTypes$: Observable<Action> = this.actions$.pipe(
        ofType(actions.LOAD_TRANSACTION_TYPES),
        switchMap(() =>
            this.lookupService.getTransactionTypes().pipe(
                map(types => new actions.TransactionTypesLoadedAction(types))
            )
        )
    );

    @Effect()
    initUnitsOfMeasure$: Observable<Action> = this.actions$.pipe(
        ofType(actions.INIT_UNITS_OF_MEASURE),
        withLatestFrom(this.store.select(s => s.root.unitsOfMeasure)),
        map(([action, unitsOfMeasure]) => {
            if (unitsOfMeasure !== undefined) {
                return {
                    type: actions.FAKE_ACTION,
                    payload: '[Root] Units of measure already loaded'
                };
            }

            return new actions.LoadUnitsOfMeasureAction();
        })
    );

    @Effect()
    loadUnitsOfMeasure$: Observable<Action> = this.actions$.pipe(
        ofType(actions.LOAD_UNITS_OF_MEASURE),
        switchMap(() =>
            this.lookupService.getUnitsOfMeasure().pipe(
                map(response => new actions.LoadUnitsOfMeasureSucceededAction(response))
            )
        )
    );

    @Effect()
    initCustomerTypes$: Observable<Action> = this.actions$.pipe(
        ofType(actions.INIT_CUSTOMER_TYPES),
        withLatestFrom(this.store.select(s => s.root.customerTypes)),
        map(([action, customerTypes]) => {
            if (customerTypes !== undefined) {
                return {
                    type: actions.FAKE_ACTION,
                    payload: '[Root] Customer types already loaded'
                };
            }

            return new actions.LoadCustomerTypesAction();
        })
    );

    @Effect()
    loadCustomerTypes$: Observable<Action> = this.actions$.pipe(
        ofType(actions.LOAD_CUSTOMER_TYPES),
        switchMap(() =>
            this.lookupService.getCustomerTypes().pipe(
                map(response => new actions.LoadCustomerTypesSucceededAction(response))
            )
        )
    );

    @Effect()
    initSuffixes$: Observable<Action> = this.actions$.pipe(
        ofType(actions.INIT_SUFFIXES),
        withLatestFrom(this.store.select(s => s.root.suffixes)),
        map(([action, suffixes]) => {
            if (suffixes !== undefined) {
                return {
                    type: actions.FAKE_ACTION,
                    payload: '[Root] Suffixes already loaded'
                };
            }

            return new actions.LoadSuffixesAction();
        })
    );

    @Effect()
    loadSuffixes$: Observable<Action> = this.actions$.pipe(
        ofType(actions.LOAD_SUFFIXES),
        switchMap(() =>
            this.lookupService.getSuffixes().pipe(
                map(response => new actions.LoadSuffixesSucceededAction(response))
            )
        )
    );

    @Effect()
    loadEditions$: Observable<Action> = this.actions$.pipe(
        ofType(actions.LOAD_EDITIONS),
        switchMap(() =>
            this.lookupService.getEditionsForCombobox(true).pipe(
                map(editions => new actions.EditionsLoadedAction(editions.items))
            )
        )
    );

    @Effect()
    loadFrequencyOptions$: Observable<Action> = this.actions$.pipe(
        ofType(actions.LOAD_FREQUENCY_OPTIONS),
        switchMap(() =>
            this.lookupService.getFrequencyOptions().pipe(
                map(options => new actions.FrequencyOptionsLoadedAction(options))
            )
        )
    );

    constructor(
        private actions$: Actions,
        private store: Store<appReducer.State>,
        private tenantService: TenantServiceProxy,
        private lookupService: CommonLookupServiceProxy
    ) { }
}
